import { Flex, Text } from '@chakra-ui/react';
import {
  Button,
  FileUpload,
  FormFeedback,
  Logo,
  Modal
} from '@morressier/alembic';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import {
  deleteUploadedFile,
  getOrganizationSignedPrivateUploadUrl
} from 'src/api/queries';
import { AxiosProgressEvent } from 'axios';
import { getFileExtension, getReferenceName } from 'src/utils/file';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import axiosInstance from 'src/api/client';
import { getAllowedExtensions } from 'src/components/FormField/FormFieldSingleFileUpload';

const LogoUrlFileUpload = ({
  organizationId,
  field,
  tempField,
  label,
  isRequired = false
}: {
  organizationId: string;
  field: string;
  tempField: string;
  label: string;
  isRequired?: boolean;
}) => {
  const {
    register,
    formState: { errors },
    resetField,
    setValue,
    setError,
    watch,
    clearErrors
  } = useFormContext();

  const allowedFileFormats = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg']
  };

  const maxSizeMB = 25;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const error = get(errors, 'logoFile') || get(errors, field);

  const value = watch(field);

  const displayName = watch(tempField);

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    const accepted = acceptedFiles[0];
    resetField(field);
    resetField(tempField);
    // Clear any existing upload/validation error before starting a new upload
    clearErrors(field);
    clearErrors('logoFile');

    if (accepted) {
      displayName?.fileId && deleteUploadedFile(displayName?.fileId);

      const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
        const progress = Math.floor((loaded * 100) / total);

        fileToUpload = {
          ...fileToUpload,
          progress
        };

        setValue(tempField, fileToUpload);
      };

      const fileExtension = getFileExtension(accepted.name);
      let fileToUpload: ISubmissionFile = {
        name: accepted.name,
        referenceName: getReferenceName(accepted.name),
        progress: 0,
        isUploading: true,
        extension: fileExtension,
        url: ''
      };

      try {
        setValue(tempField, fileToUpload);

        const uploadParams = await getOrganizationSignedPrivateUploadUrl(
          organizationId,
          fileExtension,
          false
        );
        if (!uploadParams) return;

        const formData = new FormData();
        formData.append('file', accepted, accepted.name);

        const {
          data: { id, file_url, original_filename }
        } = await axiosInstance.put<{
          id: string;
          file_url: string;
          original_filename: string;
        }>(uploadParams.signed_url, formData, {
          onUploadProgress
        });

        fileToUpload = {
          ...fileToUpload,
          fileId: id,
          url: file_url,
          name: original_filename,
          extension: fileExtension
        };
        setValue(tempField, fileToUpload);
        if (error) clearErrors('logoFile');
      } catch (e) {
        setError(field, {
          type: 'upload-error',
          message: `Error uploading file ${(e as Error).message}`
        });
      } finally {
        setValue(tempField, {
          ...fileToUpload,
          isUploading: false
        });
      }
    }
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] = rejectedFiles => {
    if (rejectedFiles[0]) {
      const rejectedFileError = rejectedFiles[0].errors[0];
      if (rejectedFileError?.code === 'file-invalid-type') {
        setError(field, {
          type: rejectedFileError.code,
          message:
            `File type must be one of ${getAllowedExtensions(allowedFileFormats).join(', ')}`
        });
      }
      if (rejectedFileError?.code === ErrorCode.FileTooLarge) {
        setError(field, {
          type: 'upload-error',
          message: `Max file size reached. Upload file lesser than or equal to ${maxSizeMB}MB`
        });
      }
    }
  };

  return (
    <Flex flexDirection="column" flex="1" gap={2}>
      <FieldLabel label={label} subText="" isRequired={isRequired} />
      <FileUpload
        uploadButtonText="Select file"
        multiple={false}
        onDrop={onDrop}
        onDropRejected={onDropRejected}
        maxFiles={1}
        maxSize={25 * 1000000}
        isInvalid={!!error}
        accept={allowedFileFormats}
        {...register('logoFile', {
          validate: isRequired
            ? () => {
                if (displayName?.fileId || value) {
                  return true;
                }
                return 'Journal logo is required';
              }
            : undefined
        })}
      />
      <Text textStyle="textContext" color="textSecondary">
        Accepted format: {getAllowedExtensions(allowedFileFormats).join(', ')}.
        Max size: {maxSizeMB}MB
      </Text>
      <Flex alignItems="center" gap={1} width={'100%'}>
        {displayName?.isUploading ? (
          <span>
            {displayName.name}: Uploading {displayName.progress}%
          </span>
        ) : (
          <>
            {(displayName?.fileId || value) && (
              <>
                <Logo
                  src={displayName?.url ?? value}
                  maxWidth="200px"
                  maxH="40px"
                  minH="40px"
                  alt="Client Logo"
                  onClick={() => setIsPreviewOpen(true)}
                  cursor="pointer"
                />
                <Modal
                  isOpen={isPreviewOpen}
                  onClose={() => setIsPreviewOpen(false)}
                  width="32rem"
                >
                  <Flex direction="column" align="center" gap={4} padding={8}>
                    <Text textStyle="headingGroup">Preview</Text>
                    <img
                      src={displayName?.url ?? value}
                      alt="Logo preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain'
                      }}
                    />
                    <Flex justifyContent="flex-end" width="100%">
                      <Button
                        variant="secondary"
                        onClick={() => setIsPreviewOpen(false)}
                      >
                        Close
                      </Button>
                    </Flex>
                  </Flex>
                </Modal>
              </>
            )}
          </>
        )}
      </Flex>
      {error?.message ? (
        <FormFeedback variant="error">{error?.message}</FormFeedback>
      ) : null}
    </Flex>
  );
};

export default LogoUrlFileUpload;
